'use client'

import NextImage from 'next/image'
import { startTransition, useCallback, useEffect, useId, useMemo, useState } from 'react'
import { Activity, Check, Clock, Info } from 'lucide-react'
import { AnimatePresence } from 'motion/react'

import { getPlaylistById, YouTubePlaylistResponse } from '@/app/actions/playlists'
import { CMSLink } from '@/components/Link'
import { RevealSection } from '@/components/RevealSection'
import RichText from '@/components/RichText'
import { Button } from '@/components/ui/button'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { VideoDialog } from '@/components/VideoDialog'
import { useLenisContext } from '@/providers/Lenis'
import FacebookIcon from '@/static/logos/logo_facebook.svg'
import InstagramIcon from '@/static/logos/logo_instagram.svg'
import XIcon from '@/static/logos/logo_x.svg'
import YouTubeIcon from '@/static/logos/logo_youtube.svg'
import { cn } from '@/utilities/ui'

import { CollectionVideoPlayer } from './CollectionVideoPlayer'
import { useYouTubePlaylistPlayer } from './useYouTubePlaylistPlayer'

import type { PlaylistBlock, Video } from '@/payload-types'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

interface PlaylistBlockClientProps {
  hideHeader?: boolean | null
  kicker?: string | null
  header?: string | null
  copy?: DefaultTypedEditorState | null
  videos: Video[]
  populateBy?: 'collection' | 'selection' | 'youtubePlaylist'
  youtubePlaylistUrl?: string | null
  links?: PlaylistBlock['links']
  includePreviewVideo?: boolean | null
  videoEmbedUrl?: string | null
  previewVideoLabel?: string | null
}

type VideoWithUrls = Video & { videoUrl?: string; videoEmbedUrl?: string }

const playlistItemToVideo = (item: YouTubePlaylistResponse['items'][number]): VideoWithUrls => ({
  id: parseInt(item.contentDetails.videoId, 10),
  title: item.snippet.title,
  description: item.snippet.description?.slice(0, 250) + '...',
  videoUrl: `https://www.youtube.com/watch?v=${item.contentDetails.videoId}`,
  videoEmbedUrl: `https://www.youtube.com/embed/${item.contentDetails.videoId}`,
  videoDuration: 0,
  category: 'Informative',
  slug: item.contentDetails.videoId,
  updatedAt: item.snippet.publishedAt,
  createdAt: item.snippet.publishedAt,
})

const getPlaylistId = (url: string) => {
  try {
    const parsed = new URL(url)
    const list = parsed.searchParams.get('list')
    if (list) return list
    const match = parsed.pathname.match(/\/show\/([^/]+)/)
    if (match) return match[1]
  } catch {}
  return null
}

const shareIcons = [
  { platform: 'facebook', Icon: FacebookIcon, label: 'Share on Facebook' },
  { platform: 'twitter', Icon: XIcon, label: 'Share on X' },
  { platform: 'instagram', Icon: InstagramIcon, label: 'Share on Instagram' },
  { platform: 'youtube', Icon: YouTubeIcon, label: 'Share on YouTube' },
] as const

const SHARE_BUTTON_BASE_CLASSES = cn(
  'group/share-button rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-2',
)
const SHARE_BUTTON_CLASSES = cn(
  'flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-xl bg-neutral-100 p-1 font-body text-reg text-foreground transition-all duration-200 group-hover/share-button:bg-neutral-200 group-hover/share-button:opacity-80',
)

export const PlaylistBlockClient: React.FC<PlaylistBlockClientProps> = ({
  hideHeader,
  header,
  copy,
  videos: initialVideos,
  populateBy,
  youtubePlaylistUrl,
  links,
  includePreviewVideo,
  videoEmbedUrl,
  previewVideoLabel,
}) => {
  const playlistId = youtubePlaylistUrl ? getPlaylistId(youtubePlaylistUrl) : null

  const [currentVideo, setCurrentVideo] = useState<VideoWithUrls | null>(
    playlistId ? null : (initialVideos[0] as VideoWithUrls | null),
  )

  const [showAllVideos, setShowAllVideos] = useState(false)
  const [watchedVideos, setWatchedVideos] = useState<Set<string>>(new Set())
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const previewButtonLabel = previewVideoLabel || 'Watch video'
  const { isMobile } = useLenisContext()
  const mobileVideoSelectId = useId()

  const [playlist, setPlaylist] = useState<YouTubePlaylistResponse | null>(null)

  const getVideoId = (video: VideoWithUrls) => {
    const match = video.videoEmbedUrl?.match(/\/embed\/([^?&]+)/)
    return match ? match[1] : null
  }

  const videoId = currentVideo ? getVideoId(currentVideo) : null

  const uploadMedia =
    currentVideo?.video != null && typeof currentVideo.video === 'object'
      ? currentVideo.video
      : null

  const showLarge =
    currentVideo != null &&
    (populateBy === 'youtubePlaylist' ? Boolean(videoId) : Boolean(videoId || uploadMedia))

  useEffect(() => {
    if (!playlistId) return
    let cancelled = false

    const fetchPlaylist = async () => {
      const res = await getPlaylistById(playlistId)

      if (!res.success) return

      const data = res.data

      if (cancelled) return

      if (!data?.items?.length) return

      setPlaylist(data)
      setCurrentVideo(playlistItemToVideo(data.items[0]!))
    }

    fetchPlaylist()

    return () => {
      cancelled = true
    }
  }, [youtubePlaylistUrl, playlistId])

  const videos = useMemo(() => {
    if (playlistId) {
      if (!playlist?.items) return []
      return playlist.items.map(playlistItemToVideo)
    }
    return initialVideos
  }, [playlistId, playlist, initialVideos])

  const videoWatched = (video: Video) => {
    if (!video.slug) return false
    return watchedVideos.has(String(video.slug))
  }

  const markVideoAsWatched = useCallback((slug: string) => {
    localStorage.setItem(`video-watched-${slug}`, 'true')
    setWatchedVideos((prev) => new Set(prev).add(slug))
  }, [])

  const showWatchMore = videos.length > 5
  const displayedVideos = showAllVideos || isMobile ? videos : videos.slice(0, 5)

  const currentVideoIndex =
    currentVideo != null ? displayedVideos.findIndex((v) => v.slug === currentVideo.slug) : -1

  const advanceToNextVideo = useCallback(() => {
    if (currentVideo) markVideoAsWatched(currentVideo.slug)
    if (displayedVideos.length === 0) return
    const nextIndex = currentVideoIndex >= 0 ? (currentVideoIndex + 1) % displayedVideos.length : 0
    const nextVideo = displayedVideos[nextIndex]
    if (nextVideo) setCurrentVideo(nextVideo as VideoWithUrls)
  }, [currentVideo, currentVideoIndex, displayedVideos, markVideoAsWatched])

  const handleNearComplete = useCallback(() => {
    if (currentVideo?.slug) markVideoAsWatched(currentVideo.slug)
  }, [currentVideo, markVideoAsWatched])

  const mobileSelectValue = currentVideoIndex >= 0 ? `playlist-video-${currentVideoIndex}` : ''

  const { playerContainerRef } = useYouTubePlaylistPlayer({
    videoId,
    hasUploadMedia: Boolean(uploadMedia),
    onNearComplete: handleNearComplete,
    onStateChange: (event) => {
      if (event.data === window.YT?.PlayerState?.ENDED) {
        advanceToNextVideo()
      }
    },
  })

  useEffect(() => {
    const watched = new Set<string>()
    videos.forEach((video) => {
      if (video.slug) {
        const id = String(video.slug)
        if (localStorage.getItem(`video-watched-${id}`) === 'true') {
          watched.add(id)
        }
      }
    })
    startTransition(() => {
      setWatchedVideos(watched)
    })
  }, [videos])

  if (!playlistId && initialVideos.length === 0) return null

  const handleSocialMediaShare = async (
    socialMedia: 'facebook' | 'twitter' | 'instagram' | 'youtube',
  ) => {
    const collectionOrSelection = populateBy === 'collection' || populateBy === 'selection'
    const shareUrl = collectionOrSelection
      ? window.location.href
      : currentVideo?.videoUrl?.trim() || ''

    switch (socialMedia) {
      case 'facebook':
        await navigator.clipboard.writeText(shareUrl)
        window.open(`https://www.facebook.com`, '_blank')
        break
      case 'twitter':
        await navigator.clipboard.writeText(shareUrl)
        window.open(`https://www.twitter.com/share?url=${shareUrl}`, '_blank')
        break
      case 'instagram':
        await navigator.clipboard.writeText(shareUrl)
        window.open(`https://www.instagram.com`, '_blank')
        break
      case 'youtube':
        await navigator.clipboard.writeText(shareUrl)
        if (populateBy === 'youtubePlaylist') {
          window.open(`${currentVideo?.videoUrl}`, '_blank')
        } else {
          window.open(`https://www.youtube.com`, '_blank')
        }
        break
    }
  }

  return (
    <div className="wrapper">
      {!hideHeader && (
        <RevealSection stagger className="mb-8 flex flex-col gap-6 lg:mb-10">
          {!hideHeader && header && (
            <h2 className="text-center font-display text-3xl text-balance lg:text-4xl xl:text-5xl">
              {header}
            </h2>
          )}
          {!hideHeader && copy && (
            <div className="mx-auto max-w-[70ch] text-center font-family-body text-base font-normal text-balance text-card-foreground lg:text-xl">
              <RichText data={copy} enableGutter={false} />
            </div>
          )}
          {((Array.isArray(links) && links.length > 0) ||
            (includePreviewVideo && videoEmbedUrl)) && (
            <div className="flex flex-row flex-wrap items-center justify-center gap-4">
              {links?.map(({ link }, i) => (
                <CMSLink key={i} {...link} appearance="default" />
              ))}
              {includePreviewVideo && videoEmbedUrl && (
                <Button variant="outline" onClick={() => setIsDialogOpen(true)}>
                  {previewButtonLabel}
                </Button>
              )}
            </div>
          )}
          <VideoDialog
            videoTitle={header || previewButtonLabel}
            videoEmbedUrl={includePreviewVideo ? videoEmbedUrl : null}
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
          />
        </RevealSection>
      )}
      <div className="rounded-xl border border-border px-4 py-6 wrapper:px-6">
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[1.5fr_2fr] xl:grid-cols-[1fr_2fr] xl:gap-10">
          <RevealSection className="flex w-full flex-col items-center gap-4 lg:items-start [&>div]:w-full">
            <AnimatePresence>
              {!isMobile &&
                displayedVideos.map((video, index) => (
                  <button
                    key={video.slug}
                    type="button"
                    onClick={() => setCurrentVideo(video as VideoWithUrls)}
                    className={cn(
                      'min-h-20 w-full cursor-pointer rounded-xl border border-transparent bg-neutral-50 px-2 py-4 text-left transition-all duration-200 hover:border-evergreen-200/50 hover:bg-evergreen-100/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-2 xl:px-4 xl:py-6',
                      currentVideo?.slug === video.slug
                        ? 'border-evergreen-200/50 bg-evergreen-100/50'
                        : 'border-transparent bg-neutral-50',
                    )}
                  >
                    <div className="flex flex-row justify-between gap-2">
                      <div className="flex flex-row gap-3 xl:gap-6">
                        <span
                          className={cn(
                            'my-auto flex aspect-square size-7 items-center justify-center rounded-full bg-neutral-200 font-family-body text-sm font-semibold text-neutral-600 transition-colors duration-200 xl:size-10 xl:text-base',
                            currentVideo?.slug === video.slug
                              ? 'bg-evergreen-800 text-evergreen-100'
                              : 'bg-neutral-200 text-neutral-600',
                          )}
                        >
                          {index + 1}
                        </span>
                        <div className="flex flex-col gap-1">
                          <span className="font-family-body text-base font-semibold text-balance text-foreground xl:text-base">
                            {video.title}
                          </span>
                          {populateBy !== 'youtubePlaylist' && (
                            <div className="flex flex-row gap-6">
                              <div className="flex flex-row gap-1">
                                <Clock className="my-auto size-2 text-neutral-600 xl:size-3" />
                                <span className="font-family-body text-xs text-neutral-600 xl:text-sm">
                                  {video.videoDuration} min
                                </span>
                              </div>
                              <div className="flex flex-row gap-1">
                                <Info className="my-auto size-2 text-neutral-600 xl:size-3" />
                                <span className="font-family-body text-xs text-neutral-600 xl:text-sm">
                                  {video.category}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <span className="my-auto flex aspect-square size-5 items-center justify-center xl:size-7">
                        {currentVideo?.slug !== video.slug ? (
                          videoWatched(video) ? (
                            <Check className="size-4 rounded-full bg-evergreen-500 p-0.5 text-white xl:size-5" />
                          ) : (
                            <Check className="size-4 rounded-full bg-neutral-100 p-0.5 text-neutral-300 xl:size-5" />
                          )
                        ) : (
                          <Activity className="size-4 text-evergreen-500 xl:size-5" />
                        )}
                      </span>
                    </div>
                  </button>
                ))}
            </AnimatePresence>
            <AnimatePresence>
              {isMobile && (
                <>
                  <label
                    className="mx-0 mt-0 -mb-2 self-start text-left font-family-body text-base font-medium text-muted-foreground"
                    htmlFor={mobileVideoSelectId}
                  >
                    Select a video to watch
                  </label>
                  <NativeSelect
                    value={mobileSelectValue}
                    onChange={(e) => {
                      const match = e.target.value.match(/^playlist-video-(\d+)$/)
                      if (match) {
                        const video = displayedVideos[parseInt(match[1], 10)]
                        if (video) setCurrentVideo(video as VideoWithUrls)
                      }
                    }}
                    className="w-full lg:hidden"
                    id={mobileVideoSelectId}
                  >
                    <NativeSelectOption value="" disabled>
                      Select a video
                    </NativeSelectOption>
                    {displayedVideos.map((video, index) => (
                      <NativeSelectOption key={video.slug} value={`playlist-video-${index}`}>
                        {video.title}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </>
              )}
            </AnimatePresence>
            {showWatchMore && !showAllVideos && !isMobile && (
              <Button
                onClick={() => setShowAllVideos(true)}
                variant="accent"
                className="font-family-body"
              >
                Watch more
              </Button>
            )}
            {showWatchMore && showAllVideos && !isMobile && (
              <Button
                onClick={() => setShowAllVideos(false)}
                variant="accent"
                className="font-family-body"
              >
                Watch less
              </Button>
            )}
          </RevealSection>

          <RevealSection className="flex w-full min-w-0 flex-col gap-8">
            {currentVideo && showLarge && (
              <div className="relative aspect-video w-full overflow-hidden rounded-xl">
                <div className="absolute inset-0">
                  {populateBy === 'youtubePlaylist' ? (
                    <div ref={playerContainerRef} className="size-full [&>iframe]:size-full" />
                  ) : uploadMedia ? (
                    <CollectionVideoPlayer
                      video={uploadMedia}
                      onEnded={advanceToNextVideo}
                      onNearComplete={handleNearComplete}
                    />
                  ) : videoId ? (
                    <div ref={playerContainerRef} className="size-full [&>iframe]:size-full" />
                  ) : null}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-6 min-[1150px]:flex-row">
              <div className="flex grow flex-col gap-4 min-[1150px]:w-2/3">
                {currentVideo?.title && (
                  <h3 className="font-family-body text-lg font-semibold text-balance text-foreground min-[1150px]:text-xl">
                    {currentVideo.title}
                  </h3>
                )}
                {currentVideo?.description && (
                  <p className="font-family-body text-base text-neutral-500 min-[1150px]:text-lg">
                    {currentVideo.description}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 grow-0 flex-col justify-start gap-4">
                <p className="font-family-body text-lg text-foreground min-[1150px]:text-xl">
                  Share
                </p>
                <div className="flex flex-row gap-2">
                  {shareIcons.map(({ platform, Icon, label }) => (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => handleSocialMediaShare(platform)}
                      aria-label={label}
                      className={cn(SHARE_BUTTON_BASE_CLASSES, SHARE_BUTTON_CLASSES)}
                    >
                      <NextImage
                        src={Icon}
                        alt=""
                        width={24}
                        height={24}
                        className="w-6"
                        aria-hidden
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </RevealSection>
        </div>
      </div>
    </div>
  )
}
